/**
 * SEAM S1 — the schedules wire contract, IMPORTED from the api zod inferences,
 * never transcribed. If either side invented, renamed or dropped a field, `tsc`
 * fails here rather than at a customer's screen.
 *
 * `import type` only: `verbatimModuleSyntax` erases these at build, so no api
 * module graph (hono, zod, drizzle, postgres) reaches the web bundle.
 */
import type {
  Schedule,
  VehicleOdometer,
} from '../../../../api/src/modules/schedules/routes';
import type {
  MaintenanceAlert,
  TrackerHealthAlert,
  PowerAlert,
  IgnitionAlert,
  TamperAlert,
  IdleAlert,
  DataDelayedAlert,
  DriverUnlinkAlert,
  DriverAbsentAlert,
  SosAlert,
  FeedItemBase,
} from '../../../../api/src/modules/alerts/routes';
import type { DriverRef } from '@/features/drivers/types';

export type { Schedule, VehicleOdometer, MaintenanceAlert, DriverUnlinkAlert, DriverAbsentAlert, SosAlert };

/**
 * 🔴 SLICE D1 — THE CANONICAL WEB FEED UNION, pinned HERE (the lowest feed module;
 * `alerts/types.ts` re-exports it, so defining it there would be a cycle).
 *
 * The api adds `driver` as an INTERSECTION on the envelope
 * (`FeedItemZ.and(z.object({ driver }))`), whose inferred type `(A | … | K) &
 * { driver }` TypeScript cannot narrow back to a member — every `item is X` guard
 * and every `Exclude` below would stop compiling. The api also exports the bare
 * union `FeedItemBase`; we distribute `& { driver? }` across it with a distributive
 * conditional, restoring a real discriminated union.
 *
 * `driver` is OPTIONAL here (the Dart side's "tolerate the field absent" posture):
 * it keeps a bare member assignable to `member & { driver? }`, so every existing
 * narrowing and fixture stays valid, and `driverOf` softens absent → null at read.
 */
export type FeedItem = FeedItemBase extends infer T
  ? T extends unknown
    ? T & { driver?: DriverRef }
    : never
  : never;

export type { FeedItemBase };

/** Pinned to the wire enums, not re-declared. */
export type ScheduleTrigger = Schedule['trigger'];
export type ScheduleState = Schedule['state'];

/** The `/maintenance` filter chips. `due` is the default window the API's
 *  `/schedules/due` already defines (30 days OR 1,000 km); `overdue` and `all`
 *  are server-side filters on the list route. Never a client-side filter over a
 *  keyset page, which would show fewer rows than a page holds. */
export type MaintenanceFilter = 'due' | 'overdue' | 'all';

/**
 * 🔴 THE THIRD LEG OF THE FEED UNION (slice 28). A geofence crossing carries no
 * `type` key; an overspeed episode is `type: 'overspeed'`; a maintenance reminder
 * is `maintenance_due` / `maintenance_overdue`.
 *
 * This narrowing lives ONCE, here, pinned to the wire union — the same discipline
 * `isOverspeed` follows — so the list rows, the map, the CSV and the day summary
 * cannot each re-test it with a slightly different stringly check.
 *
 * 🔴 A MAINTENANCE ITEM HAS NO `observedLat`/`observedLon`, AND THAT IS THE
 * DESIGN. See `MaintenanceRow.tsx` for why it must never be given one.
 */
export function isMaintenance(item: FeedItem): item is MaintenanceAlert {
  return (
    'type' in item && (item.type === 'maintenance_due' || item.type === 'maintenance_overdue')
  );
}

/**
 * 🔴 THE FOURTH LEG OF THE FEED UNION (slice 13f). A tracker-health event —
 * `tracker_offline` / `tracker_online` / `gps_fix_lost`.
 *
 * Like the maintenance narrowing, it lives once, pinned to the wire union, so the
 * list rows, the map, the CSV and the day summary cannot each re-test it with a
 * slightly different stringly check. It sits beside `hasObservedPosition` because
 * that predicate now DEPENDS on it — a tracker-health event has no coordinates.
 */
export function isTrackerHealth(item: FeedItem): item is TrackerHealthAlert {
  return (
    'type' in item &&
    (item.type === 'tracker_offline' ||
      item.type === 'tracker_online' ||
      item.type === 'gps_fix_lost')
  );
}

/**
 * 🔴 SLICE 54 — the SIXTH and SEVENTH legs of the feed union: a power event and an
 * ignition event.
 *
 * A power event (`power_cut` critical / `power_restored` info) MAY carry a location
 * — the vehicle's LAST VALID GPS fix at the time external power was lost — but its
 * `lat`/`lon` are NULLABLE (a device that never got a fix has none) and are NOT the
 * `observedLat`/`observedLon` a positioned item carries: they are the last KNOWN
 * location, an honest fallback, not where the event happened. 🔴 SLICE 56b — an
 * ignition event (`ignition_on` / `ignition_off`, info) NOW carries the SAME kind of
 * last-known pin (nullable `observedLat`/`observedLon`, the last valid fix at/before
 * the transition, or both null); it is read only through `ignitionPin`. So all three
 * are excluded from `PositionedFeedItem` below and handled by their own rows;
 * `powerPin`/`ignitionPin` are the one place those coordinates are read, so a wire
 * rename touches one helper.
 */
export function isPower(item: FeedItem): item is PowerAlert {
  return 'type' in item && (item.type === 'power_cut' || item.type === 'power_restored');
}

export function isIgnition(item: FeedItem): item is IgnitionAlert {
  return 'type' in item && (item.type === 'ignition_on' || item.type === 'ignition_off');
}

/**
 * 🔴 SLICE 55 — the EIGHTH leg of the feed union: a TAMPER alert (`type: 'tamper'`).
 * Anti-theft; always `critical`. Like a power_cut it MAY carry a location, and the
 * meaning is the same: `observedLat`/`observedLon` are the LAST KNOWN fix (the
 * frame's own coords when it was a valid fix, else the last valid fix at/before),
 * NULLABLE, and NOT the `observedLat`/`observedLon` a positioned item guarantees.
 * So it is excluded from `PositionedFeedItem` below and rendered by its own row;
 * `tamperPin` is the one place its coordinates are read, so a wire rename touches
 * one helper. Named positively, pinned to the wire union — never `!isPower(...)`.
 */
export function isTamper(item: FeedItem): item is TamperAlert {
  return 'type' in item && item.type === 'tamper';
}

/**
 * 🔴 SLICE 66k — the NINTH leg of the feed union: an IDLE alert (`type: 'idle'`).
 * Ignition on, stationary past the tenant's idle threshold. Always `info`, exactly
 * like an ignition event — it never nags the unread badge. Like a power/ignition/
 * tamper event it MAY carry a location, and its `observedLat`/`observedLon` are
 * NULLABLE (the crossing fix's own valid coords, or both null when the device had no
 * valid fix), so it is excluded from `PositionedFeedItem` below and rendered by its
 * own row; `idlePin` is the one place its coordinates are read, so a wire rename
 * touches one helper. Named positively, pinned to the wire union — never
 * `!isIgnition(...)`, which would be a claim about the size of a union that grows.
 */
export function isIdle(item: FeedItem): item is IdleAlert {
  return 'type' in item && item.type === 'idle';
}

/**
 * 🔴 SLICE 76 — the TENTH leg of the feed union: a DATA-DELAYED alert
 * (`type: 'data_delayed'`). A tracker's real GPS frames are arriving > 15 min
 * behind their fix time (the SIM-credit / weak-signal buffering case). Always
 * `warning`, in the SAME "tracker link unhealthy" family as `tracker_offline` /
 * `gps_fix_lost` — so, like them, it has NO coordinates: the point stream is
 * stale, there is no trustworthy location, and none must be invented. It is
 * therefore excluded from `PositionedFeedItem` below and rendered by
 * `DataDelayedRow`, a plain Link to the vehicle, never a map. Named positively,
 * pinned to the wire union — never `!isTrackerHealth(...)`, a claim about the size
 * of a union that grows.
 */
export function isDataDelayed(item: FeedItem): item is DataDelayedAlert {
  return 'type' in item && item.type === 'data_delayed';
}

/**
 * 🔴 SLICE D2 — the TWELFTH leg of the feed union: a DRIVER-UNLINK-REQUEST alert
 * (`type: 'driver_unlink_requested'`). A driver cannot unlink an owner-assigned
 * vehicle himself; from the Raad Driver app he can only REQUEST it, which lands in
 * the owner's feed (`severity: 'warning'`). A request is NOT a positioned event — it
 * carries no `observedLat`/`observedLon` at all — so it is excluded from
 * `PositionedFeedItem` below and rendered by `DriverUnlinkRow`, a plain markable
 * button that never reaches the map (the same discipline as tracker-health and
 * data-delayed). Named positively, pinned to the wire union — never
 * `!isGeofenceCrossing(...)`, a claim about the size of a union that grows.
 */
export function isDriverUnlink(item: FeedItem): item is DriverUnlinkAlert {
  return 'type' in item && item.type === 'driver_unlink_requested';
}

/**
 * 🔴 SLICE D2 — the THIRTEENTH leg of the feed union: a DRIVER-ABSENT alert
 * (`type: 'driver_absent'`). A vehicle with a PERMANENT driver whose Raad Driver
 * app is activated moved ≥ 5 min with NO open shift on it — "moved without its
 * driver", `severity: 'warning'`. UNLIKE the driver-unlink request (which is not
 * positioned), this is a POSITIONED item: its `observedLat`/`observedLon` are the
 * fix where the unattended movement was observed and are NON-NULL, so it STAYS in
 * `PositionedFeedItem` (not added to the `Exclude` below) and `hasObservedPosition`
 * narrows it in — the map centres on it exactly like overspeed/moved-while-parked.
 * Named positively, pinned to the wire union.
 */
export function isDriverAbsent(item: FeedItem): item is DriverAbsentAlert {
  return 'type' in item && item.type === 'driver_absent';
}

/**
 * 🔴 SLICE 85 — the FOURTEENTH leg of the feed union: an SOS alarm (`type: 'sos'`).
 * A driver pressed the panic button; always `critical`. Like a power/tamper event its
 * `observedLat`/`observedLon` are the LAST VALID FIX pinned at detection (NEVER the
 * alarm frame's frozen coords — position-27) and are NULLABLE (the device may have had
 * no valid fix), so it is NOT a positioned item: it is excluded from `PositionedFeedItem`
 * below and read only through `sosPin`. Named positively, pinned to the wire union —
 * never `!isGeofenceCrossing(...)`, a claim about the size of a union that grows.
 */
export function isSos(item: FeedItem): item is SosAlert {
  return 'type' in item && item.type === 'sos';
}

/**
 * The tamper event's LAST KNOWN location, or null when the device had no valid fix
 * to fall back on. The single reader of the tamper wire coordinates — the row and
 * any map focus go through here — so the "pin only from a valid fix, never the
 * frozen/invalid frame" rule lives in one place (mirrors `powerPin`).
 */
export function tamperPin(item: TamperAlert): { lat: number; lon: number } | null {
  return item.observedLat != null && item.observedLon != null
    ? { lat: item.observedLat, lon: item.observedLon }
    : null;
}

/**
 * 🔴 SLICE 66k — the idle event's location, or null when the device had no valid fix.
 * Mirrors `ignitionPin`/`tamperPin` exactly: the wire carries the crossing fix's own
 * valid coords as nullable `observedLat`/`observedLon` (both set, or both null —
 * never an invented point). It is the SINGLE reader of the idle wire coordinates —
 * the row, the fleet-map focus and the map's point collection all go through here —
 * so the "pin only from a valid fix" rule lives in one place. `IdleAlert` stays
 * excluded from `PositionedFeedItem` below because these coords are NULLABLE, and a
 * positioned item guarantees a non-null point.
 */
export function idlePin(item: IdleAlert): { lat: number; lon: number } | null {
  return item.observedLat != null && item.observedLon != null
    ? { lat: item.observedLat, lon: item.observedLon }
    : null;
}

/**
 * 🔴 SLICE 85 — the SOS alarm's LAST VALID FIX, or null when the device had no valid
 * fix at detection. Mirrors `tamperPin`/`idlePin` exactly: the wire carries nullable
 * `observedLat`/`observedLon` (both set, or both null — never an invented point, never
 * the alarm frame's frozen coords). The SINGLE reader of the SOS wire coordinates — the
 * row, the fleet-map focus and the map's point collection all go through here — so the
 * "pin only from a valid fix" rule lives in one place. `SosAlert` stays excluded from
 * `PositionedFeedItem` below because these coords are NULLABLE, and a positioned item
 * guarantees a non-null point.
 */
export function sosPin(item: SosAlert): { lat: number; lon: number } | null {
  return item.observedLat != null && item.observedLon != null
    ? { lat: item.observedLat, lon: item.observedLon }
    : null;
}

/**
 * 🔴 SLICE 56b — the ignition event's LAST KNOWN location, or null when the device
 * had no valid fix to fall back on. Mirrors `powerPin`/`tamperPin` exactly: the wire
 * now carries the vehicle's last valid fix at/before the transition frame as nullable
 * `observedLat`/`observedLon` (both set, or both null — never an invented point). It
 * is the SINGLE reader of the ignition wire coordinates — the row, the fleet-map
 * focus and the map's point collection all go through here — so the "pin only from a
 * valid fix" rule lives in one place. `IgnitionAlert` stays excluded from
 * `PositionedFeedItem` below precisely because these are a last-KNOWN fallback, not
 * an OBSERVED point where the ignition turned.
 */
export function ignitionPin(item: IgnitionAlert): { lat: number; lon: number } | null {
  return item.observedLat != null && item.observedLon != null
    ? { lat: item.observedLat, lon: item.observedLon }
    : null;
}

/**
 * The power event's LAST KNOWN location, or null when the device has no valid fix
 * to fall back on. The single reader of the power wire coordinates — the row, the
 * fleet-map focus and the map's point collection all go through here, so the "pin
 * only from a valid fix, never the frozen/invalid frame" rule (PLAN AC-6) lives in
 * one place and a wire-field rename is a one-line change.
 */
export function powerPin(item: PowerAlert): { lat: number; lon: number } | null {
  // The wire carries the last-valid fix as nullable `observedLat`/`observedLon`
  // (both set, or both null). PowerAlert is still excluded from PositionedFeedItem
  // below precisely because these are nullable — a positioned item guarantees a
  // non-null point, a power pin does not.
  return item.observedLat != null && item.observedLon != null
    ? { lat: item.observedLat, lon: item.observedLon }
    : null;
}

/**
 * True when a feed item has an observed position to put on a map. A geofence
 * crossing and an overspeed episode do; a maintenance reminder, a tracker-health
 * event, a power event and an ignition event do NOT — a service falling due, a
 * tracker going silent, and an engine turning off did not happen at an OBSERVED
 * point. A power or ignition event's last-known pin is handled separately
 * (`powerPin`/`ignitionPin`), never as an `observedLat`/`observedLon`, because it is
 * a fallback location, not the place the event occurred.
 *
 * 🔴 SPELT POSITIVELY, NEVER `!isOverspeed(...)` or a bare "not the maintenance
 * one". A narrowing written as "not the other one" is a claim about the SIZE of
 * the union, and the union keeps growing: an old `!isMaintenance` would have handed
 * a coordinate-less tracker-health or ignition event to the map with undefined
 * lat/lon — the exact class of bug `isGeofenceCrossing`'s comment warns about.
 * `tsc` proves the exclusion at each call site via `PositionedFeedItem`.
 *
 * 🔴 A TAMPER alert is excluded too (slice 55). Like a power_cut its coordinates are
 * a LAST KNOWN fix, not an OBSERVED point — handing it to the map as
 * `observedLat`/`observedLon` would read it as where the tampering happened, which
 * it is not, and would be `undefined` on the positioned path anyway.
 *
 * 🔴 AN IDLE alert is excluded too (slice 66k). Its `observedLat`/`observedLon` are
 * NULLABLE (the crossing fix, or both null), so it is a `idlePin` fallback leg, not a
 * positioned item — the same treatment as ignition.
 *
 * 🔴 A DATA-DELAYED alert is excluded too (slice 76). Like tracker-health it has NO
 * coordinates at all — a stale point stream has no trustworthy location — so it must
 * never reach the map (`alert.observedLat/Lon` would be undefined). It is a plain
 * Link to the vehicle, exactly like `tracker_offline`.
 *
 * 🔴 A DRIVER-UNLINK-REQUEST alert is excluded too (slice D2). Like tracker-health
 * and data-delayed it has NO coordinates at all — a request is not a positioned
 * event — so it must never reach the map. It is a plain markable row.
 *
 * 🔴 AN SOS alarm is excluded too (slice 85). Its `observedLat`/`observedLon` are
 * NULLABLE (the last valid fix, or both null when the device had none), so like
 * tamper/idle it is a `sosPin` fallback leg, not a positioned item.
 */
export type PositionedFeedItem = Exclude<
  FeedItem,
  | MaintenanceAlert
  | TrackerHealthAlert
  | PowerAlert
  | IgnitionAlert
  | TamperAlert
  | IdleAlert
  | DataDelayedAlert
  | DriverUnlinkAlert
  | SosAlert
>;

export function hasObservedPosition(item: FeedItem): item is PositionedFeedItem {
  return (
    !isMaintenance(item) &&
    !isTrackerHealth(item) &&
    !isPower(item) &&
    !isIgnition(item) &&
    !isTamper(item) &&
    !isIdle(item) &&
    !isDataDelayed(item) &&
    !isDriverUnlink(item) &&
    !isSos(item)
  );
}
